import React, { useEffect, useState } from 'react';
import {
  StyledChips,
  StyledCloseIcon,
  StyledSidebarWrapper,
  StyledWarningIcon,
  StyledWarningWrapper,
} from 'pages/Firearms/RegisteredFirearmsMapPage/FirearmDataSidebar/style';
import { Typography } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import { EIconTypes } from 'constants/Icons';
import { colors } from 'theme/colors';
import Icon from 'components/atoms/Icon/Icon';
import SearchFilterStore from 'state/SearchFilterStore';
import { EChipsVariants } from 'components/atoms/Chips';
import Grid from '@mui/material/Grid';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { useDictionary } from 'models/dictionaryContext';
import { useLang } from 'models/langContext';
import { IAmmunition, IAncillary } from 'models/ancillaries';
import { LEGAL_ID } from 'pages/Firearms/RegisteredFirearmsMapPage/FirearmsLocationMap';
import AmmunitionDataListItem from 'pages/AncillariesPage/AncillariesMap/AncillariesLocationMap/AmmunitionDataSidebar/AmmunitionDataListItem';
import { StyledPageTitle } from 'theme/styles';

export enum ELegalityFilter {
  legal = 'd90f05bf-6baf-4f86-9ca3-9501af6901ea',
  illegal = 'ae5de668-67ac-4650-af26-23dd0cb97c89',
}

interface IAmmunitionDataSidebar {
  width?: string;
  title?: string;
  handleBack?: (arg?: boolean) => void;
}

const AmmunitionDataSidebar = ({
  width = 'auto',
  title,
  handleBack,
}: IAmmunitionDataSidebar) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { state, getState } = useDictionary();
  const { selectedLanguage } = useLang();
  const { state: routerState }: any = useLocation();
  const [ammunitions, setAmmunitions] = useState<IAmmunition[]>([]);
  const [filteredAmmunitions, setFilteredAmmunitions] = useState<IAmmunition[]>([]);
  const [chipsData, setChipsData] = useState<{ illegal: number; legal: number }>({
    illegal: 0,
    legal: 0,
  });
  const [filter, setFilter] = useState<ELegalityFilter>();

  useEffect(() => {
    (async function initDictionaries() {
      getState();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);
  const prepareAmmunition = (ammunitionData: IAmmunition[]) => {
    let illegalCount = 0;
    let legalCount = 0;

    let filteredItems: IAmmunition[] = [];

    if (filter) {
      filteredItems = ammunitionData.filter(
        (ammunition) => ammunition.legalityUniqueId === filter,
      );
    } else {
      filteredItems = ammunitionData;
    }

    filteredItems.map((ammunition) => {
      if (ammunition.legalityUniqueId !== LEGAL_ID) {
        illegalCount++;
      } else {
        legalCount++;
      }
      return false;
    });

    setFilteredAmmunitions(filteredItems);
    setChipsData({ illegal: illegalCount, legal: legalCount });
  };

  useEffect(() => {
    const handleSetAmmunition = (data: any) => {
      const ammunitionData: IAmmunition[] = data.detail.data as IAmmunition[];
      setAmmunitions(ammunitionData);
      prepareAmmunition(ammunitionData);
      setFilter(undefined);
    };
    document.addEventListener('setAmmunitionsEvent', handleSetAmmunition);
    return () => {
      document.removeEventListener('setAmmunitionsEvent', handleSetAmmunition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    prepareAmmunition(ammunitions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  if (!filteredAmmunitions.length) {
    return null;
  }

  return (
    <StyledSidebarWrapper id="ammunitions-data-sidebar" style={{ width: width }}>
      <Grid sx={{ justifyContent: "space-between" }}>
        <div>
          {handleBack && (
            <Button
              onClick={() => {
                handleBack();
                SearchFilterStore.setAmmunition([]);
                SearchFilterStore.setMapBounds(undefined);
              }}
              label={t('general.backToGroupPreview.button')}
              icon={EIconTypes.left}
              size={EButtonSizes.small}
              variant={EButtonVariants.outlined}
            />
          )}
        </div>
        <StyledCloseIcon
          onClick={() => {
            if (handleBack) handleBack(false);
            SearchFilterStore.setAmmunition([]);
            SearchFilterStore.setMapBounds(undefined);
          }}
        >
          <Icon type={EIconTypes.close} color={colors.black} />
        </StyledCloseIcon>
      </Grid>
      {title && (
        <StyledPageTitle variant="h5" style={{ marginBottom: '16px' }}>
          {t(title)}
        </StyledPageTitle>
      )}
      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
        {t('ancillaries.ancillariesLocation.legality')}
      </Typography>
      <Grid container size={{ xs: 12 }} columnSpacing={1}>
        {!!chipsData.legal && (
          <Grid size={{ xs: 6 }}>
            <StyledChips
              text={`${t('ancillaries.ancillariesLocation.legal.label')}: ${chipsData.legal}`}
              fullWidth
              variant={EChipsVariants.success}
              onClick={() => {
                setFilter((prev) =>
                  prev === ELegalityFilter.legal ? undefined : ELegalityFilter.legal,
                );
              }}
            />
          </Grid>
        )}
        {!!chipsData.illegal && (
          <Grid size={{ xs: 6 }}>
            <StyledChips
              text={`${t('ancillaries.ancillariesLocation.illegal.label')}: ${chipsData.illegal
                }`}
              fullWidth
              variant={EChipsVariants.error}
              onClick={() => {
                setFilter((prev) =>
                  prev === ELegalityFilter.illegal ? undefined : ELegalityFilter.illegal,
                );
              }}
            />
          </Grid>
        )}
      </Grid>
      <Grid
        container
        sx={{
          margin: '15px 0',
          paddingTop: '15px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: {
            xs: `1px solid ${colors.gray200}`,
          },
        }}
      >
        {filteredAmmunitions.length > 50 && (
          <StyledWarningWrapper>
            <Grid container spacing={2}>
              <Grid size={{ xs: 2 }}>
                <StyledWarningIcon>
                  <Icon type={EIconTypes.info} color={colors.white} />
                </StyledWarningIcon>
              </Grid>
              <Grid size={{ xs: 10 }}>
                <Typography variant="caption">
                  {t('ancillaries.ancillariesLocation.ammunition.caption')}
                </Typography>
              </Grid>
            </Grid>
          </StyledWarningWrapper>
        )}

        <Grid size={{ xs: 12 }}>
          <Button
            label={t('ancillaries.ancillariesLocation.showAmmunition.label')}
            variant={EButtonVariants.contained}
            onClick={() => {
              navigate(`${ERouteLinks.Ancillaries}?expand=ammunition&fromMap=true`, {
                state: {
                  ancillaryType: 'ammunition',
                  ammunitionFormValues: routerState?.ancillaryQuery,
                  ammunitionOptions: {
                    ownerOptions: routerState?.options?.ownerOptions,
                    keeperOptions: routerState?.options?.keeperOptions,
                  },
                },
              });
            }}
            size="small"
            fullWidth
          />
        </Grid>
      </Grid>

      {filteredAmmunitions.length <= 50 &&
        filteredAmmunitions.map((ammunition) => (
          <AmmunitionDataListItem data={ammunition} key={ammunition.uniqueId} state={state} />
        ))}
    </StyledSidebarWrapper>
  );
};

export default AmmunitionDataSidebar;
